/*
 * Copyright 2026 North Carolina State University
 */

import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { normalizeRequestStatus, requestStatusColor } from '../../../utils/requestStatus';
import { formatDateTime } from '../../../utils/timeFormat';

/**
 * The request thread: one conversation between the admins and the project team
 * for the life of a request.
 *
 * This is why a request needs no revision status. Instead of sending a request
 * back and waiting for a formal resubmit, an admin asks for the change here,
 * the client replies here and edits the form, and the edit itself is recorded
 * as a field-level diff (see RequestChangeLog).
 *
 * Admin messages are stored on the request as admin_responses (that array
 * doubles as the action log, so a status move with a comment shows up as a
 * message); client messages are stored as client_comments. Both are merged by
 * time here so the conversation reads in order.
 */

// How an admin_responses entry is introduced when it is more than a message.
const ACTION_META = {
  status_change: { sentence: 'moved the request' },
  admin_edit: { label: 'Edited', color: 'warning', sentence: 'edited the request' },
  // Pre-rename actions, still in the history of older requests.
  approve: { label: 'Completed', color: 'success', sentence: 'completed the request' },
  reject: { label: 'Rejected', color: 'error', sentence: 'rejected the request' },
  revision_requested: { label: 'Changes Requested', color: 'info', sentence: 'asked for changes' },
  reopen: { label: 'Reopened', color: 'primary', sentence: 'reopened the request' }
};

const personName = (person, fallback = '') => {
  if (!person) return fallback;
  if (typeof person === 'string') return fallback;
  const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  return name || person.email || fallback;
};

/** Merge both sides of the conversation into one time-ordered list. */
export const buildThread = (request) => {
  const entries = [];

  (request?.admin_responses || []).forEach((r, i) => {
    if (!r.comment || !r.comment.trim()) return;
    const meta = ACTION_META[r.action_type];
    const movedTo = r.action_type === 'status_change' && r.status_after
      ? normalizeRequestStatus(r.status_after)
      : null;
    entries.push({
      key: r._id || `admin-${i}`,
      side: 'admin',
      author: personName(r.responded_by, 'Admin'),
      at: r.responded_at,
      comment: r.comment,
      chipLabel: movedTo || meta?.label || null,
      chipColor: movedTo ? requestStatusColor(movedTo) : (meta?.color || 'default'),
      sentence: movedTo
        ? `marked the request ${movedTo}`
        : (meta?.sentence || null)
    });
  });

  (request?.client_comments || []).forEach((c, i) => {
    entries.push({
      key: c._id || `client-${i}`,
      side: 'client',
      author: personName(c.commented_by, c.commented_by_name || 'Project team'),
      at: c.commented_at,
      comment: c.comment,
      chipLabel: null,
      chipColor: 'default',
      sentence: null
    });
  });

  return entries.sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
};

/**
 * @param {Object}   request
 * @param {boolean}  isAdmin   post as an admin (admin comment endpoint)
 * @param {Function} onChanged reload the request after a post
 * @param {boolean}  readOnly  hide the composer entirely
 */
const RequestThread = ({ request, isAdmin = false, onChanged, readOnly = false }) => {
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  const entries = useMemo(() => buildThread(request), [request]);
  const canPost = !readOnly && (request?.permissions?.can_comment ?? isAdmin);

  const handleSend = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      const url = isAdmin
        ? `/api/admin/requests/${request._id}/comments`
        : `/api/requests/${request._id}/comments`;
      await axios.post(url, { comment });
      toast.success(isAdmin ? 'Reply sent to the project team' : 'Reply sent to the facility');
      setComment('');
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Request thread post error:', err);
      toast.error(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        {isAdmin
          ? 'Everything posted here goes to the project team and they are notified. Ask for form changes here — either side can then edit the form, and every change is logged.'
          : 'Use this thread to talk to the facility about this request. If they ask for a change, edit the request form and the change is recorded automatically.'}
      </Alert>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No messages yet.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          {entries.map((e) => (
            <Box
              key={e.key}
              sx={{
                p: 1.25,
                borderRadius: 1,
                textAlign: 'left',
                bgcolor: e.side === 'admin' ? 'grey.100' : 'rgba(25, 118, 210, 0.06)',
                borderLeft: '3px solid',
                borderColor: e.side === 'admin' ? 'grey.400' : 'primary.light'
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }} flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  label={e.side === 'admin' ? 'Facility' : 'Project team'}
                />
                {e.chipLabel && (
                  <Chip size="small" label={e.chipLabel} color={e.chipColor} variant="outlined" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {e.author} · {formatDateTime(e.at)}
                </Typography>
              </Stack>
              {e.sentence && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}
                >
                  {e.author} {e.sentence}:
                </Typography>
              )}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{e.comment}</Typography>
            </Box>
          ))}
        </Stack>
      )}

      {canPost ? (
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={isAdmin ? 'Write a message to the project team…' : 'Write a message to the facility…'}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !comment.trim()}
            startIcon={<SendIcon />}
          >
            Send
          </Button>
        </Stack>
      ) : !readOnly && (
        <Typography variant="caption" color="text.secondary">
          This request is closed, so the thread is read-only. Submit a new request to start again.
        </Typography>
      )}
    </Box>
  );
};

export default RequestThread;
